import { z } from "zod";
import {
  isValidSsn,
  SSN_VALIDATION_MESSAGE,
} from "../../../../../../utils/ssn-validation.ts";

export const US_BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] as const;
export const US_MARITAL_STATUSES = ["Single", "Married", "Widowed", "Separated", "Divorced"] as const;
export const US_EMERGENCY_RELATIONSHIPS = [
  "Mother", "Father", "Sister", "Brother", "Guardian", "Spouse",
] as const;

const name = (label: string) => z.string().trim().min(1, `${label} is required`).max(100).regex(/^[a-zA-Z '-]+$/, `${label} contains invalid characters`);

export const usPersonalSchema = z.object({
  firstName: name("First name"),
  middleName: z.string().trim().max(100).optional(),
  lastName: name("Last name"),
  personalEmail: z.string().trim().min(1, "Personal email is required").email("Enter a valid email address"),
  gender: z.enum(["male", "female", "others"], { required_error: "Gender is required" }),
  bloodGroup: z.union([z.enum(US_BLOOD_GROUPS), z.literal("")]),
  dob: z.string().min(1, "Date of birth is required").refine((value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    if (today < new Date(today.getFullYear(), date.getMonth(), date.getDate())) age--;
    return age >= 18;
  }, "You must be at least 18 years old"),
  joiningDate: z.string().min(1, "Date of joining is required"),
  maritalStatus: z.union([z.enum(US_MARITAL_STATUSES), z.literal("")]),
  anniversaryDate: z.string().optional(),
});

export const usContactSchema = z.object({
  phoneNumber: z.string().regex(/^\d{10}$/, "Phone number must contain 10 digits."),
  emergencyContactName: name("Emergency contact name"),
  emergencyContactNumber: z.string().regex(/^\d{10}$/, "Emergency contact number must contain 10 digits."),
  emergencyRelationship: z.enum(US_EMERGENCY_RELATIONSHIPS, { required_error: "Relationship is required" }),
  presentAddress: z.string().trim().min(1, "Present address is required."),
  permanentAddress: z.string().trim().min(1, "Permanent address is required."),
}).superRefine((values, context) => {
  if (values.phoneNumber === values.emergencyContactNumber) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["emergencyContactNumber"], message: "Emergency contact number must be different" });
  }
});

export const usIdentitySchema = z.object({
  ssn: z.string().trim(),
  passportNumber: z.string().trim().toUpperCase(),
  zipCode: z.string().trim().regex(/^\d{5}$/, "Enter a valid 5-digit ZIP Code (e.g., 12345)"),
}).superRefine((values, context) => {
  if (!values.ssn && !values.passportNumber) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["ssn"], message: "Enter an SSN or passport number" });
    return;
  }
  if (values.ssn && !isValidSsn(values.ssn)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["ssn"], message: SSN_VALIDATION_MESSAGE });
  }
  if (values.passportNumber && !/^[A-Z0-9]{6,9}$/.test(values.passportNumber)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["passportNumber"],
      message: "Enter a valid US passport number with 6 to 9 letters or digits (for example, A12345678)",
    });
  }
});

export type UsPersonalValues = z.infer<typeof usPersonalSchema>;
export type UsContactValues = z.infer<typeof usContactSchema>;
export type UsIdentityValues = z.infer<typeof usIdentitySchema>;
export type UsPreJoinValues = UsPersonalValues & UsContactValues & UsIdentityValues;
